import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';

const TurnstileWidget = forwardRef(({ sitekey, onVerify, onError, onExpire, reset = false }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    reset: () => {
      resetWidget();
    },
    getWidgetId: () => widgetIdRef.current,
  }));

  const renderWidget = () => {
    if (!window.turnstile || !containerRef.current) {
      console.log('Turnstile not ready or container not mounted');
      return;
    }

    // Remove existing widget if any
    if (widgetIdRef.current) {
      try {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      } catch (e) {
        console.warn('Error removing widget:', e);
      }
    }

    // Render new widget
    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: sitekey,
        callback: (token) => {
          console.log('Turnstile verified with token:', token);
          if (onVerify) onVerify(token);
        },
        'error-callback': () => {
          console.error('Turnstile verification failed');
          if (onError) onError();
        },
        'expired-callback': () => {
          console.log('Turnstile verification expired');
          if (onExpire) onExpire();
        },
        theme: 'light',
        size: 'normal',
        language: 'en',
      });
      isInitializedRef.current = true;
    } catch (e) {
      console.error('Error rendering Turnstile:', e);
    }
  };

  const resetWidget = () => {
    console.log('Resetting Turnstile widget');
    if (window.turnstile && widgetIdRef.current) {
      try {
        window.turnstile.reset(widgetIdRef.current);
        // Also clear any error states
        if (onExpire) onExpire();
      } catch (e) {
        console.warn('Error resetting widget:', e);
        // If reset fails, try re-rendering
        renderWidget();
      }
    } else {
      // If widget doesn't exist, render it
      renderWidget();
    }
  };

  useEffect(() => {
    // Check if Turnstile is already loaded
    if (window.turnstile) {
      renderWidget();
      return;
    }

    // Load Turnstile script
    console.log('Loading Turnstile script...');
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      console.log('Turnstile script loaded');
      renderWidget();
    };
    
    script.onerror = () => {
      console.error('Failed to load Turnstile script');
    };
    
    document.body.appendChild(script);

    // Cleanup
    return () => {
      if (window.turnstile && widgetIdRef.current) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          console.warn('Error cleaning up widget:', e);
        }
      }
    };
  }, []);

  // Reset when reset prop changes
  useEffect(() => {
    if (reset) {
      resetWidget();
    }
  }, [reset]);

  return <div ref={containerRef} className="turnstile-container" />;
});

TurnstileWidget.displayName = 'TurnstileWidget';

export default TurnstileWidget;